import {Formik} from 'formik';
import {useContext, useState, useCallback} from 'react';

import {FormWithButtons} from '@flexinet/ui-components';

import {useNavigate} from "react-router-dom";
import {stage} from "../../../config/stage";
import {UploadFormComponent} from "./UploadFormComponent";
import {UploadFormSchema} from "./UploadFormSchema";
import {ImportProgressModal} from "../components/ImportProgressModal";
import {ImportResultsModal} from "../components/ImportResultsModal";
import {DuplicateResolutionModal} from "../components/DuplicateResolutionModal";
import {fileProcessingService, ParsedDomainData, ValidationError} from "../services/FileProcessingService";
import {bulkImportService, ImportResult, ImportError, ImportProgress, DuplicateConflict, DuplicateCheckResult} from "../services/BulkImportService";
import {AuthContext} from "../../../context/AuthContext.tsx";
import {
    showErrorNotifications,
    showSuccessNotification,
    showValidationErrorNotification,
    showFileProcessingErrorNotification
} from "../../../components/ServiceNotifications.tsx";

// Form data interface for the upload workflow
export interface ImportFormData {
    fileName: string;
    defaultRegistrarId: string;
    skipDuplicates: boolean;
    updateExisting: boolean;
}


export const UploadForm = () => {
    const navigate = useNavigate();
    const { user } = useContext(AuthContext);
    
    // State for import workflow
    const [isImporting, setIsImporting] = useState(false);
    const [importProgress, setImportProgress] = useState<ImportProgress>({
        total: 0,
        processed: 0,
        successful: 0,
        failed: 0,
        isComplete: false
    });
    const [importResults, setImportResults] = useState<ImportResult | null>(null);
    const [showProgressModal, setShowProgressModal] = useState(false);
    const [showResultsModal, setShowResultsModal] = useState(false);
    const [showDuplicateModal, setShowDuplicateModal] = useState(false);
    const [duplicateCheckResult, setDuplicateCheckResult] = useState<DuplicateCheckResult | null>(null);
    const [pendingImportData, setPendingImportData] = useState<{
        formValues: ImportFormData;
        // FNP-960: the call sites already convert parse/validation failures to
        // the ImportError shape ({domainName, error, rowNumber}) before storing
        // them here, so that is the stored type. Annotating these as
        // ValidationError was wrong in the other direction and the compiler
        // caught it.
        validData: ParsedDomainData[];
        parseErrors: ImportError[];
        validationErrors: ImportError[];
    } | null>(null);
    const [uploadedFile, setUploadedFile] = useState<File | null>(null);

    const initialValues: ImportFormData = {
        fileName: '',
        defaultRegistrarId: '',
        skipDuplicates: true,
        updateExisting: false
    };

    const handleProgressUpdate = useCallback((progress: ImportProgress) => {
        setImportProgress(progress);
        
        if (progress.isComplete) {
            setIsImporting(false);
            setShowProgressModal(false);
            // Results will be shown via the results modal
        }
    }, []);

    const handleImportComplete = useCallback((results: ImportResult) => {
        setImportResults(results);
        setShowResultsModal(true);
    }, []);

    const handleDuplicateResolution = useCallback(async (resolvedConflicts: DuplicateConflict[]) => {
        if (!user?.username || !pendingImportData) {
            showErrorNotifications(new Error('Missing required data for import'));
            return;
        }

        try {
            setShowDuplicateModal(false);
            setIsImporting(true);
            setShowProgressModal(true);
            setImportProgress({
                total: 0,
                processed: 0,
                successful: 0,
                failed: 0,
                isComplete: false
            });

            // Apply duplicate resolution to filter the data
            const resolutionResult = bulkImportService.applyDuplicateResolution(
                pendingImportData.validData,
                resolvedConflicts
            );

            // Import only the domains marked for creation
            if (resolutionResult.toImport.length > 0) {
                const importOptions = {
                    defaultRegistrarId: pendingImportData.formValues.defaultRegistrarId,
                    skipDuplicates: false, // We've already resolved duplicates
                    updateExisting: false, // Handle updates separately if needed
                    userId: user.username
                };

                const results = await bulkImportService.importDomainNames(
                    resolutionResult.toImport,
                    importOptions,
                    handleProgressUpdate
                );

                // Include any parsing/validation errors in the final results
                const allErrors: ImportError[] = [
                    ...pendingImportData.parseErrors,
                    ...pendingImportData.validationErrors,
                    ...results.errors
                ];

                // Generate skip details from resolution result
                const skipDetailsFromResolution = resolutionResult.toSkip.map(item => ({
                    domainName: item.domainName,
                    normalizedName: item.domainName.toLowerCase().trim(),
                    rowNumber: item.rowNumber,
                    skipReason: 'Skipped by user choice in duplicate resolution',
                    skipCategory: 'other' as const
                }));

                // Combine skip details
                const allSkipDetails = [...results.skipDetails, ...skipDetailsFromResolution];
                
                // Update skip reasons summary
                const updatedSkipReasons = {
                    ...results.skipReasons,
                    other: results.skipReasons.other + resolutionResult.toSkip.length
                };

                const finalResults: ImportResult = {
                    ...results,
                    skipped: results.skipped + resolutionResult.toSkip.length,
                    errors: allErrors,
                    failed: results.failed + pendingImportData.parseErrors.length + pendingImportData.validationErrors.length,
                    skipDetails: allSkipDetails,
                    skipReasons: updatedSkipReasons
                };

                handleImportComplete(finalResults);
            } else {
                // All domains were skipped - generate skip details from conflicts
                const skipDetailsFromConflicts = resolvedConflicts.map(conflict => {
                    let skipCategory: 'existing_in_system' | 'duplicate_in_file' | 'other';
                    let skipReason: string;

                    if (conflict.existsInSystem && conflict.existsInFile) {
                        skipCategory = 'existing_in_system';
                        skipReason = 'Domain exists in your account and appears multiple times in the file';
                    } else if (conflict.existsInSystem) {
                        skipCategory = 'existing_in_system';
                        skipReason = 'Domain already exists in your account';
                    } else if (conflict.existsInFile) {
                        skipCategory = 'duplicate_in_file';
                        skipReason = 'Duplicate domain in uploaded file (keeping first occurrence)';
                    } else {
                        skipCategory = 'other';
                        skipReason = conflict.skipReason || 'Skipped by user choice';
                    }

                    return {
                        domainName: conflict.domainName,
                        normalizedName: conflict.normalizedName,
                        rowNumber: conflict.rowNumber,
                        skipReason: skipReason,
                        skipCategory: skipCategory,
                        existingDomainId: conflict.existingDomain?.id
                    };
                });

                // Calculate skip reasons summary
                const skipReasons = {
                    existingInSystem: skipDetailsFromConflicts.filter(d => d.skipCategory === 'existing_in_system').length,
                    duplicateInFile: skipDetailsFromConflicts.filter(d => d.skipCategory === 'duplicate_in_file').length,
                    validationError: 0,
                    other: skipDetailsFromConflicts.filter(d => d.skipCategory === 'other').length
                };

                const finalResults: ImportResult = {
                    successful: 0,
                    failed: pendingImportData.parseErrors.length + pendingImportData.validationErrors.length,
                    skipped: resolvedConflicts.length,
                    errors: [...pendingImportData.parseErrors, ...pendingImportData.validationErrors],
                    processedDomains: [],
                    skipDetails: skipDetailsFromConflicts,
                    skipReasons: skipReasons
                };

                setIsImporting(false);
                setShowProgressModal(false);
                handleImportComplete(finalResults);
            }

        } catch (error) {
            console.error('Import failed after duplicate resolution:', error);
            setIsImporting(false);
            setShowProgressModal(false);
            showErrorNotifications(error);
        } finally {
            setPendingImportData(null);
        }
    }, [user?.username, pendingImportData, handleProgressUpdate, handleImportComplete]);

    const handleDuplicateCancel = useCallback(() => {
        setShowDuplicateModal(false);
        setDuplicateCheckResult(null);
        setPendingImportData(null);
    }, []);

    const handleDownloadErrors = useCallback(() => {
        if (!importResults || (importResults.errors.length === 0 && importResults.skipped === 0)) return;

        try {
            // Enhanced CSV content with separate sections for skipped and failed domains
            const csvRows: string[] = [];
            
            // Helper function to escape CSV values
            const escapeCsvValue = (value: string): string => {
                if (value.includes(',') || value.includes('"') || value.includes('\n')) {
                    return `"${value.replace(/"/g, '""')}"`;
                }
                return value;
            };

            // Helper function to convert row array to CSV string
            const rowToCsv = (row: string[]): string => {
                return row.map(escapeCsvValue).join(',');
            };

            // Collect failed domains (actual errors during processing)
            const failedDomains = importResults.errors
                .filter(error => error.errorType !== 'skip' && error.errorType !== 'duplicate')
                .map(error => ({
                    rowNumber: error.rowNumber,
                    domainName: error.domainName,
                    reason: error.error,
                    category: error.errorType || 'unknown',
                    normalizedName: error.normalizedName || '',
                    existingDomainId: ''
                }));

            // Collect skipped domains from multiple sources
            const skippedDomains: Array<{
                rowNumber: number;
                domainName: string;
                reason: string;
                category: string;
                normalizedName: string;
                existingDomainId: string;
            }> = [];

            // Add skipped domains from errors (for backward compatibility)
            importResults.errors
                .filter(error => error.errorType === 'skip' || error.errorType === 'duplicate')
                .forEach(error => {
                    skippedDomains.push({
                        rowNumber: error.rowNumber,
                        domainName: error.domainName,
                        reason: error.error,
                        category: error.skipCategory || 'other',
                        normalizedName: error.normalizedName || '',
                        existingDomainId: error.existingDomainId || ''
                    });
                });

            // Add skipped domains from skip details (primary source)
            (importResults.skipDetails || []).forEach(detail => {
                // Avoid duplicates by checking if already added from errors
                const alreadyExists = skippedDomains.some(
                    existing => existing.rowNumber === detail.rowNumber && 
                               existing.domainName === detail.domainName
                );
                
                if (!alreadyExists) {
                    skippedDomains.push({
                        rowNumber: detail.rowNumber,
                        domainName: detail.domainName,
                        reason: detail.skipReason,
                        category: detail.skipCategory,
                        normalizedName: detail.normalizedName,
                        existingDomainId: detail.existingDomainId || ''
                    });
                }
            });

            // Sort both arrays by row number for better readability
            failedDomains.sort((a, b) => a.rowNumber - b.rowNumber);
            skippedDomains.sort((a, b) => a.rowNumber - b.rowNumber);

            // Add report header with summary
            csvRows.push('Domain Import Report');
            csvRows.push(`Generated: ${new Date().toISOString()}`);
            csvRows.push('');
            csvRows.push('SUMMARY');
            csvRows.push(`Successful: ${importResults.successful}`);
            csvRows.push(`Failed: ${failedDomains.length}`);
            csvRows.push(`Skipped: ${skippedDomains.length}`);
            csvRows.push('');

            // Add skip reasons breakdown if available
            if (importResults.skipReasons) {
                csvRows.push('SKIP REASONS BREAKDOWN');
                csvRows.push(`Existing in System: ${importResults.skipReasons.existingInSystem}`);
                csvRows.push(`Duplicate in File: ${importResults.skipReasons.duplicateInFile}`);
                csvRows.push(`Validation Error: ${importResults.skipReasons.validationError}`);
                csvRows.push(`Other: ${importResults.skipReasons.other}`);
                csvRows.push('');
            }

            // Add skipped domains section
            if (skippedDomains.length > 0) {
                csvRows.push('SKIPPED DOMAINS');
                csvRows.push(rowToCsv(['Row Number', 'Domain Name', 'Skip Reason', 'Category', 'Normalized Name', 'Existing Domain ID']));
                
                skippedDomains.forEach(domain => {
                    csvRows.push(rowToCsv([
                        domain.rowNumber.toString(),
                        domain.domainName,
                        domain.reason,
                        domain.category,
                        domain.normalizedName,
                        domain.existingDomainId
                    ]));
                });
                csvRows.push('');
            }

            // Add failed domains section
            if (failedDomains.length > 0) {
                csvRows.push('FAILED DOMAINS');
                csvRows.push(rowToCsv(['Row Number', 'Domain Name', 'Error Reason', 'Error Type', 'Normalized Name']));
                
                failedDomains.forEach(domain => {
                    csvRows.push(rowToCsv([
                        domain.rowNumber.toString(),
                        domain.domainName,
                        domain.reason,
                        domain.category,
                        domain.normalizedName
                    ]));
                });
            }

            const csvContent = csvRows.join('\n');

            // Download the file
            const blob = new Blob([csvContent], { type: 'text/csv' });
            const url = URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = `domain-import-report-${new Date().toISOString().split('T')[0]}.csv`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            URL.revokeObjectURL(url);

            showSuccessNotification('Import report downloaded successfully');
        } catch (error) {
            showErrorNotifications(error);
        }
    }, [importResults]);

    const onSubmit = async (values: ImportFormData, { resetForm }: { resetForm: () => void }) => {
        if (!user?.username) {
            showErrorNotifications(new Error('User not authenticated'));
            return;
        }

        if (!uploadedFile) {
            showErrorNotifications(new Error('No file selected'));
            return;
        }

        try {
            // Step 1: Parse the uploaded file
            const parseResult = await fileProcessingService.parseFile(uploadedFile);
            
            if (parseResult.errors.length > 0 && parseResult.data.length === 0) {
                // File parsing failed completely - show specific error
                showFileProcessingErrorNotification(
                    'File parsing failed completely. Please check your file format and try again.',
                    uploadedFile.name
                );
                throw new Error(`File parsing failed: ${parseResult.errors.map(e => e.message).join(', ')}`);
            }

            // Step 2: Validate the parsed data
            const validationResult = fileProcessingService.validateDomainData(parseResult.data);
            
            if (validationResult.errors.length > 0 && validationResult.valid.length === 0) {
                // Validation failed completely - show specific error
                const errorSummary = generateValidationErrorSummary(validationResult.errors);
                showValidationErrorNotification(
                    'Data validation failed completely. Please fix the errors in your file and try again.',
                    errorSummary
                );
                throw new Error(`Data validation failed: ${validationResult.errors.map(e => e.message).join(', ')}`);
            }

            // Step 3: Check for duplicates with user isolation
            console.log('Starting duplicate detection for', validationResult.valid.length, 'domains');
            const duplicateResult = await bulkImportService.detectDuplicates(validationResult.valid);
            console.log('Duplicate detection completed:', {
                existingDomains: duplicateResult.existingDomains.size,
                fileDuplicates: duplicateResult.fileDuplicates.size,
                conflicts: duplicateResult.conflicts.length
            });

            // Step 4: Handle duplicates if found
            if (duplicateResult.conflicts.length > 0) {
                // Store pending import data and show duplicate resolution modal
                const parseErrors = parseResult.errors.map(e => ({
                    domainName: e.value,
                    error: e.message,
                    rowNumber: e.rowNumber
                }));
                
                const validationErrors = validationResult.errors.map(e => ({
                    domainName: e.value,
                    error: e.message,
                    rowNumber: e.rowNumber
                }));

                setPendingImportData({
                    formValues: values,
                    validData: validationResult.valid,
                    parseErrors,
                    validationErrors
                });
                
                setDuplicateCheckResult(duplicateResult);
                setShowDuplicateModal(true);
                return; // Wait for user resolution
            }

            // Step 5: No duplicates found, proceed with import
            setIsImporting(true);
            setShowProgressModal(true);
            setImportProgress({
                total: 0,
                processed: 0,
                successful: 0,
                failed: 0,
                isComplete: false
            });

            const importOptions = {
                defaultRegistrarId: values.defaultRegistrarId,
                skipDuplicates: values.skipDuplicates,
                updateExisting: values.updateExisting,
                userId: user.username
            };

            const results = await bulkImportService.importDomainNames(
                validationResult.valid,
                importOptions,
                handleProgressUpdate
            );

            // Include any parsing/validation errors in the final results
            const allErrors = [
                ...parseResult.errors.map(e => ({
                    domainName: e.value,
                    error: e.message,
                    rowNumber: e.rowNumber
                })),
                ...validationResult.errors.map(e => ({
                    domainName: e.value,
                    error: e.message,
                    rowNumber: e.rowNumber
                })),
                ...results.errors
            ];

            const finalResults: ImportResult = {
                ...results,
                errors: allErrors,
                failed: results.failed + parseResult.errors.length + validationResult.errors.length,
                // Ensure skip details and reasons are preserved from the original results
                skipDetails: results.skipDetails || [],
                skipReasons: results.skipReasons || {
                    existingInSystem: 0,
                    duplicateInFile: 0,
                    validationError: 0,
                    other: 0
                }
            };

            handleImportComplete(finalResults);

            // Reset form after successful import
            if (finalResults.successful > 0) {
                resetForm();
                setUploadedFile(null);
            }

        } catch (error) {
            console.error('Import failed:', error);
            setIsImporting(false);
            setShowProgressModal(false);
            showErrorNotifications(error);
        }
    };

    const onCancel = () => {
        if (isImporting) {
            // TODO: Implement import cancellation if needed
            return;
        }
        navigate('/domain-names');
    };

    const handleCloseProgressModal = () => {
        if (!isImporting) {
            setShowProgressModal(false);
        }
    };

    const handleCloseResultsModal = () => {
        setShowResultsModal(false);
        setImportResults(null);
    };

    const generateValidationErrorSummary = (errors: ValidationError[]): string[] => {
        const summary: string[] = [];
        const errorsByType = new Map<string, number>();
        const sampleErrors: string[] = [];

        // Categorize errors
        errors.forEach(error => {
            const key = `${error.field}: ${error.message}`;
            errorsByType.set(key, (errorsByType.get(key) || 0) + 1);
            
            // Keep first few examples
            if (sampleErrors.length < 3) {
                sampleErrors.push(`Row ${error.rowNumber}: ${error.message} (${error.value})`);
            }
        });

        // Add error type summary
        errorsByType.forEach((count, errorType) => {
            summary.push(`${errorType} (${count} occurrence${count !== 1 ? 's' : ''})`);
        });

        // Add sample errors if we have them
        if (sampleErrors.length > 0) {
            summary.push('', 'Examples:');
            summary.push(...sampleErrors);
        }

        return summary;
    };

    return (
        <>
            <Formik
                initialValues={initialValues}
                validationSchema={UploadFormSchema}
                onSubmit={onSubmit}
            >
                {({
                      handleSubmit,
                      isSubmitting,
                      ...rest
                  }) => (
                    <FormWithButtons 
                        handleSubmit={handleSubmit} 
                        isSubmitting={isSubmitting || isImporting}
                        handleCancel={onCancel} 
                        debug={false && stage === 'ide'} 
                        {...rest}
                    >
                        <UploadFormComponent onFileChange={setUploadedFile} />
                    </FormWithButtons>
                )}
            </Formik>

            {/* Import Progress Modal */}
            <ImportProgressModal
                isOpen={showProgressModal}
                progress={importProgress}
                onClose={handleCloseProgressModal}
                canCancel={false} // TODO: Implement cancellation if needed
            />

            {/* Duplicate Resolution Modal */}
            {duplicateCheckResult && (
                <DuplicateResolutionModal
                    isOpen={showDuplicateModal}
                    conflicts={duplicateCheckResult.conflicts}
                    normalizedDomains={duplicateCheckResult.normalizedDomains}
                    onResolve={handleDuplicateResolution}
                    onCancel={handleDuplicateCancel}
                />
            )}

            {/* Import Results Modal */}
            {importResults && (
                <ImportResultsModal
                    isOpen={showResultsModal}
                    results={importResults}
                    onClose={handleCloseResultsModal}
                    onDownloadErrors={handleDownloadErrors}
                />
            )}
        </>
    );
};